import { logger } from '@sentry/react';
import { IWebSocketConnection } from './IWebSocketConnection';

export class ManagedWebSocketConnection implements IWebSocketConnection {
  private ws?: WebSocket;
  private connectionTimeout?: NodeJS.Timeout;
  private readonly CONNECTION_TIMEOUT_MS: number = 10000;

  private openCallback?: () => void;
  private closeCallback?: (event: CloseEvent) => void;
  private errorCallback?: () => void;
  private messageCallback?: (event: MessageEvent) => void;

  private boundOnOpen = this.handleOpen.bind(this);
  private boundOnClose = this.handleClose.bind(this);
  private boundOnError = this.handleError.bind(this);
  private boundOnMessage = this.handleMessage.bind(this);

  constructor(
    private readonly url: string,
    private readonly canvasId: string
  ) {}

  connect(): void {
    this.cleanupWebSocket();

    try {
      this.ws = new WebSocket(this.url);
      this.ws.addEventListener('message', this.boundOnMessage, false);
      this.ws.addEventListener('close', this.boundOnClose, false);
      this.ws.addEventListener('open', this.boundOnOpen, false);
      this.ws.addEventListener('error', this.boundOnError, false);

      this.connectionTimeout = setTimeout(() => {
        if (!this.ws) return;

        if (this.ws.readyState === WebSocket.OPEN) return;

        logger.warn('WebSocket connection timeout', {
          canvasId: this.canvasId,
          readyState: this.ws.readyState,
        });
        this.ws.close();
      }, this.CONNECTION_TIMEOUT_MS);
    } catch (e) {
      logger.error('Error connecting to WebSocket', {
        type: 'WebSocketError',
        message: (e as Error)?.message,
        canvasId: this.canvasId,
      });
    }
  }

  send(message: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(message);
  }

  close(code: number = 1000, reason: string = 'cleanup'): void {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      this.ws.close(code, reason);
    }
  }

  cleanup(): void {
    this.cleanupWebSocket();
  }

  isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  onOpen(callback: () => void): void {
    this.openCallback = callback;
  }

  onClose(callback: (event: CloseEvent) => void): void {
    this.closeCallback = callback;
  }

  onError(callback: () => void): void {
    this.errorCallback = callback;
  }

  onMessage(callback: (event: MessageEvent) => void): void {
    this.messageCallback = callback;
  }

  get nativeWebSocket(): WebSocket | undefined {
    return this.ws;
  }

  private cleanupWebSocket(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = undefined;
    }

    if (this.ws) {
      this.ws.removeEventListener('message', this.boundOnMessage);
      this.ws.removeEventListener('close', this.boundOnClose);
      this.ws.removeEventListener('open', this.boundOnOpen);
      this.ws.removeEventListener('error', this.boundOnError);

      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close(1000, 'cleanup');
      }

      this.ws = undefined;
    }
  }

  private handleOpen(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = undefined;
    }
    this.openCallback?.();
  }

  private handleClose(event: CloseEvent): void {
    logger.info('Lost WebSocket connection', {
      canvasId: this.canvasId,
    });
    this.closeCallback?.(event);
  }

  private handleError(): void {
    logger.error('WebSocket error occurred', {
      canvasId: this.canvasId,
    });
    this.errorCallback?.();
  }

  private handleMessage(event: MessageEvent): void {
    this.messageCallback?.(event);
  }
}
